import React, { useEffect, useState } from 'react';
import TransactionApi from '../api/TransactionApi';

// Utility function to get month names by number
const getMonthName = (monthNumber) => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  return months[monthNumber];
};

const ExpenseReport = () => {
  const [categoryExpenses, setCategoryExpenses] = useState({});
  const [totalExpense, setTotalExpense] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(Array(12).fill(0));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    getTransactions();
    // Simulate a data fetch or a task
    const fetchData = async () => {
      setIsLoading(true); // Set loading to true
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate a delay
      setIsLoading(false); // Set loading to false after the task is complete
    };

    fetchData();
  }, [selectedYear]);

  const fetchYears = async () => {
    const res = await TransactionApi.index();
    const uniqueYears = [...new Set(res.map(transaction => new Date(transaction.paid_at).getFullYear()))];
    setYears(uniqueYears);
    // Set the default selected year to the current year if available, otherwise use the first year
    if (!uniqueYears.includes(new Date().getFullYear())) {
      setSelectedYear(uniqueYears[0]);
    }
  };

  const getTransactions = async () => {
    const res = await TransactionApi.index();

    const categoryData = {};
    res.forEach((transaction) => {
      const { amount, paid_at, type, cat_name } = transaction;
      const parsedAmount = parseFloat(amount);
      const date = new Date(paid_at);
      const monthIndex = date.getMonth();
      const year = date.getFullYear();

      if (year === selectedYear && type === 'expense') {
        const categoryName = cat_name || 'Uncategorized';
        if (!categoryData[categoryName]) {
          categoryData[categoryName] = Array(12).fill(0);
        }
        categoryData[categoryName][monthIndex] += parsedAmount;
      }
    });

    const totalExpenses = Array(12).fill(0).map((_, monthIndex) => {
      return Object.values(categoryData).reduce(
        (sum, monthlyData) => sum + monthlyData[monthIndex],
        0
      );
    });

    const newCategoryExpenses = {};
    for (const [catName, monthlyData] of Object.entries(categoryData)) {
      newCategoryExpenses[catName] = monthlyData;
    }

    setCategoryExpenses(newCategoryExpenses);

    const total = totalExpenses.reduce((sum, expense) => sum + expense, 0);
    setTotalExpense(total);
    setTotalExpenses(totalExpenses);
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value));
  };

  const sum = 0;

  return (
    
    <div>
      {isLoading ? (
        <div className="loading">
          <p>Loading...</p>
          {/* Add a spinner if you want */}
        </div>
      ) : (
      <div className="mx-auto">
        <div className="flex flex-col">
          <div className="flex justify-end mb-4">
            <select value={selectedYear} onChange={handleYearChange} className="p-2 border rounded w-20">
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto shadow-md sm:rounded-lg">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 table-fixed dark:divide-gray-700">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400"> 
                        Expense Name
                      </th>
                      {Array.from({ length: 12 }, (_, i) => (
                        <th
                          key={i}
                          scope="col"
                          className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400"
                        >
                          {getMonthName(i)}
                        </th>
                      ))}
                      <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400"> 
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                    {/* Display category expenses */}
                    {Object.entries(categoryExpenses).map(([catName, expenses]) => (
                      <tr key={catName} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                        <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                          {catName}
                        </td>
                        {expenses.map((expense, index) => (
                          <td
                            key={index}
                            className="py-4 px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white"
                          >
                            {expense.toFixed(2)}
                          </td>
                          
                        ))}
                      
                        <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                          {expenses.reduce((total, num) => total + num, 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}

                    {/* Display total monthly expenses */}
                    <tr className="hover:bg-gray-100 dark:hover:bg-gray-700">
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                        Total
                      </td>
                      {totalExpenses.map((expense, index) => (
                        <td
                          key={index}
                          className="py-4 px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {expense.toFixed(2)}
                        </td>
                      ))}
                      <td className="py-4 px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">
                      {totalExpense.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                  Total Yearly Expense: {totalExpense.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default ExpenseReport;
